import React, { useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Upload, FileSpreadsheet, CheckCircle2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

// Minimal CSV parser that handles quoted fields, commas inside quotes, and newlines inside quotes.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(field);
        field = '';
      } else if (ch === '\n') {
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else if (ch === '\r') {
        // ignore
      } else {
        field += ch;
      }
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

const SAMPLE_CSV = `author_name,author_location,quote,rating,source,source_url,verified
John Smith,Liverpool,"Absolutely brilliant session, will be back!",5,trustpilot,https://uk.trustpilot.com/review/motionreality.co.uk,true
Sarah Jones,Manchester,"Great rigs and friendly staff.",5,google,https://www.google.com/maps/...,true`;

export default function ReviewCsvImport({ onClose }) {
  const queryClient = useQueryClient();
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const fileInputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) {
        setResult({ error: 'CSV appears to be empty or missing data rows.' });
        return;
      }
      const headers = rows[0].map((h) => h.trim().toLowerCase());
      const required = ['author_name', 'quote'];
      const missing = required.filter((r) => !headers.includes(r));
      if (missing.length) {
        setResult({ error: `CSV is missing required column(s): ${missing.join(', ')}` });
        return;
      }
      const idx = (name) => headers.indexOf(name);
      const mapped = rows.slice(1).filter((r) => r.length === headers.length).map((r) => {
        const rating = Number(r[idx('rating')]) || 5;
        const source = (r[idx('source')] || 'customer').trim().toLowerCase();
        const verified = String(r[idx('verified')] || '').toLowerCase() === 'true';
        const sourceUrl = idx('source_url') >= 0 ? r[idx('source_url')].trim() : '';
        const sourceLabel = source === 'google' ? 'Google' : source === 'trustpilot' ? 'Trustpilot' : 'Customer Review';
        return {
          author_name: r[idx('author_name')].trim(),
          author_location: idx('author_location') >= 0 ? r[idx('author_location')].trim() : '',
          quote: r[idx('quote')].trim(),
          rating: Math.min(5, Math.max(1, rating)),
          source: ['google', 'trustpilot', 'customer'].includes(source) ? source : 'customer',
          source_url: sourceUrl,
          source_label: sourceLabel,
          verified,
          status: 'approved',
        };
      }).filter((r) => r.author_name && r.quote);
      setParsedRows(mapped);
      if (mapped.length === 0) setResult({ error: 'No valid rows found after parsing.' });
    } catch (err) {
      setResult({ error: 'Could not read the file. Please ensure it is a valid CSV.' });
    }
  };

  const runImport = async () => {
    if (parsedRows.length === 0) return;
    setImporting(true);
    setResult(null);
    try {
      const created = await base44.entities.Review.bulkCreate(parsedRows);
      const count = Array.isArray(created) ? created.length : (created?.length || parsedRows.length);
      queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['approved-reviews'] });
      setResult({ success: true, count });
      setParsedRows([]);
      setFileName('');
    } catch (e) {
      setResult({ error: e?.message || 'Import failed. Please try again.' });
    } finally {
      setImporting(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reviews-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-[#111115] border border-white/5 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <p className="text-primary text-[10px] font-bold tracking-widest">BULK IMPORT REVIEWS (CSV)</p>
        <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="bg-[#0d0d0f] border border-white/5 p-4 mb-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="text-white/50 text-xs leading-relaxed">
            <p className="text-white/70 font-bold mb-1">CSV columns required:</p>
            <code className="text-primary/80 text-[11px]">author_name, author_location, quote, rating, source, source_url, verified</code>
            <ul className="mt-2 space-y-0.5 list-disc list-inside text-white/40">
              <li><b>author_name</b> &amp; <b>quote</b> are required; the rest are optional.</li>
              <li><b>rating</b> 1–5 (defaults to 5).</li>
              <li><b>source</b>: google, trustpilot, or customer.</li>
              <li><b>source_url</b>: direct link to the original review.</li>
              <li><b>verified</b>: true/false.</li>
              <li>Imported reviews are published as <b>approved</b> immediately.</li>
            </ul>
            <button onClick={downloadSample} className="mt-2 text-primary text-[11px] font-bold underline hover:text-primary/80">Download sample template</button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFile} className="hidden" />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          className="flex items-center gap-2 bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-wider px-5 py-2.5 transition-colors disabled:opacity-40"
        >
          <Upload className="w-4 h-4" /> {fileName ? 'CHANGE FILE' : 'CHOOSE CSV FILE'}
        </button>
        {fileName && <span className="text-white/40 text-xs">{fileName}</span>}
        {parsedRows.length > 0 && (
          <button
            onClick={runImport}
            disabled={importing}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-2.5 transition-colors disabled:opacity-40 ml-auto"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} IMPORT {parsedRows.length} REVIEW{parsedRows.length !== 1 ? 'S' : ''}
          </button>
        )}
      </div>

      {parsedRows.length > 0 && (
        <div className="mt-4 max-h-48 overflow-y-auto border border-white/5">
          <table className="w-full text-xs">
            <thead className="bg-[#0d0d0f] sticky top-0">
              <tr className="text-white/40 text-[10px] font-bold tracking-widest">
                <th className="text-left p-2">NAME</th>
                <th className="text-left p-2">SOURCE</th>
                <th className="text-left p-2">RATING</th>
                <th className="text-left p-2">QUOTE</th>
              </tr>
            </thead>
            <tbody>
              {parsedRows.slice(0, 50).map((r, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-2 text-white/70">{r.author_name}</td>
                  <td className="p-2 text-white/50 uppercase">{r.source}</td>
                  <td className="p-2 text-white/50">{r.rating}★</td>
                  <td className="p-2 text-white/50 max-w-xs truncate">{r.quote}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {parsedRows.length > 50 && <p className="p-2 text-white/30 text-[10px]">...and {parsedRows.length - 50} more</p>}
        </div>
      )}

      {result?.success && (
        <div className="mt-4 px-4 py-3 border border-green-500/30 bg-green-500/10 text-green-300 text-xs">
          Successfully imported {result.count} review{result.count !== 1 ? 's' : ''}. They are now live on the storefront.
        </div>
      )}
      {result?.error && (
        <div className="mt-4 px-4 py-3 border border-red-500/30 bg-red-500/10 text-red-300 text-xs">{result.error}</div>
      )}
    </div>
  );
}